import { useCallback, useMemo, useRef, useState } from "react";

const MAX_OPERATION_STACK_SIZE = 100;
const EMPTY_DIRTY_STATE = {
    isDirty: false,
    dirtyPairKeys: new Set(),
    dirtyPairCount: 0,
};

function asKey(value) {
    return value == null ? null : String(value);
}

function getPairKey(trackId, referenceTrackId) {
    const trackKey = asKey(trackId);
    const referenceKey = asKey(referenceTrackId);
    return trackKey && referenceKey ? `${trackKey}::${referenceKey}` : null;
}

function clonePlain(value) {
    if (value == null || typeof value !== "object") {
        return value;
    }
    return JSON.parse(JSON.stringify(value));
}

function createLocalMatchId() {
    const randomId = typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2);
    return `local-match:${randomId}`;
}

function normalizeStatus(match) {
    return String(match?.match_status || match?.effective_status || "UNMATCHED").toUpperCase();
}

function normalizeType(match) {
    return String(match?.match_type || "AUTO").toUpperCase();
}

function normalizeSourceOperationId(match) {
    const sourceId = match?.source_operation_id ?? match?.source_flight_id;
    if (sourceId === undefined || sourceId === null || sourceId === "") {
        return null;
    }
    const numericId = Number(sourceId);
    return Number.isFinite(numericId) ? numericId : sourceId;
}

function normalizeMatch(match, trackId, referenceTrackId) {
    const sourceOperationId = normalizeSourceOperationId(match);
    const isLocal = !match?.id || String(match.id).startsWith("local-match:");
    return {
        ...clonePlain(match),
        id: match?.id || createLocalMatchId(),
        schedule_draft_flight_id: asKey(match?.schedule_draft_flight_id ?? match?.draft_flight_id),
        source_operation_id: sourceOperationId,
        source_flight_id: sourceOperationId,
        track_id: asKey(match?.track_id ?? trackId),
        reference_track_id: asKey(match?.reference_track_id ?? referenceTrackId),
        match_type: normalizeType(match),
        match_status: normalizeStatus(match),
        is_valid: match?.is_valid !== false,
        _local: isLocal,
    };
}

function normalizeMatches(matches = [], trackId, referenceTrackId) {
    return (Array.isArray(matches) ? matches : [])
        .filter(Boolean)
        .map((match) => normalizeMatch(match, trackId, referenceTrackId));
}

function serializeMatchForCompare(match) {
    return {
        schedule_draft_flight_id: asKey(match?.schedule_draft_flight_id),
        source_operation_id: normalizeSourceOperationId(match),
        reference_track_id: asKey(match?.reference_track_id),
        match_type: normalizeType(match),
        match_status: normalizeStatus(match),
        is_valid: match?.is_valid !== false,
    };
}

function matchSignature(matches = []) {
    return JSON.stringify(
        (Array.isArray(matches) ? matches : [])
            .map(serializeMatchForCompare)
            .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
    );
}

function getMatchIdentity(match) {
    if (!match) {
        return null;
    }
    return asKey(match.id) || `${asKey(match.schedule_draft_flight_id) || "-"}:${asKey(match.source_operation_id) || "-"}:${normalizeType(match)}:${normalizeStatus(match)}`;
}

function getSourceKey(match) {
    const sourceId = normalizeSourceOperationId(match);
    return sourceId == null ? null : String(sourceId);
}

function hasDraftConflict(match, draftFlightId) {
    return draftFlightId && asKey(match?.schedule_draft_flight_id) === asKey(draftFlightId);
}

function hasSourceConflict(match, sourceOperationId) {
    return sourceOperationId != null && getSourceKey(match) === String(sourceOperationId);
}

function removeConflictingMatches(matches, draftFlightId, sourceOperationId) {
    return matches.filter((match) => !hasDraftConflict(match, draftFlightId) && !hasSourceConflict(match, sourceOperationId));
}

function createManualMatch({ trackId, referenceTrackId, scheduleDraftFlightId = null, sourceOperationId = null, matchStatus }) {
    return normalizeMatch(
        {
            id: createLocalMatchId(),
            schedule_draft_flight_id: scheduleDraftFlightId,
            source_operation_id: sourceOperationId,
            source_flight_id: sourceOperationId,
            match_type: "MANUAL",
            match_status: matchStatus,
            is_valid: true,
        },
        trackId,
        referenceTrackId,
    );
}

function pushOperation(stack, operation) {
    const nextStack = [...stack, operation];
    if (nextStack.length > MAX_OPERATION_STACK_SIZE) {
        return nextStack.slice(nextStack.length - MAX_OPERATION_STACK_SIZE);
    }
    return nextStack;
}

function makePair(trackId, referenceTrackId, serverMatches = [], options = {}) {
    const normalizedServerMatches = normalizeMatches(serverMatches, trackId, referenceTrackId);
    const hasServerReferenceTrackId = Object.prototype.hasOwnProperty.call(options, "serverReferenceTrackId");
    return {
        trackId: asKey(trackId),
        referenceTrackId: asKey(referenceTrackId),
        serverReferenceTrackId: asKey(hasServerReferenceTrackId ? options.serverReferenceTrackId : referenceTrackId),
        initialized: true,
        serverMatches: normalizedServerMatches,
        localMatches: normalizedServerMatches,
        undoStack: [],
        redoStack: [],
        serverSignature: matchSignature(normalizedServerMatches),
    };
}

function isPairDirty(pair) {
    if (!pair) {
        return false;
    }
    return asKey(pair.referenceTrackId) !== asKey(pair.serverReferenceTrackId) || matchSignature(pair.localMatches) !== pair.serverSignature;
}

function computeDirtyState(pairs) {
    const dirtyPairKeys = new Set();
    pairs.forEach((pair, pairKey) => {
        if (isPairDirty(pair)) {
            dirtyPairKeys.add(pairKey);
        }
    });

    return {
        isDirty: dirtyPairKeys.size > 0,
        dirtyPairKeys,
        dirtyPairCount: dirtyPairKeys.size,
    };
}

function toPayloadMatch(match) {
    const sourceOperationId = normalizeSourceOperationId(match);
    return {
        schedule_draft_flight_id: asKey(match?.schedule_draft_flight_id),
        source_operation_id: sourceOperationId,
        source_flight_id: sourceOperationId,
        reference_track_id: asKey(match?.reference_track_id),
        match_type: normalizeType(match),
        match_status: normalizeStatus(match),
    };
}

function updatePairInMap(pairs, pairKey, updater) {
    const currentPair = pairs.get(pairKey);
    if (!currentPair) {
        return pairs;
    }
    const nextPair = updater(currentPair);
    if (!nextPair || nextPair === currentPair) {
        return pairs;
    }
    const nextPairs = new Map(pairs);
    nextPairs.set(pairKey, nextPair);
    return nextPairs;
}

export function useLocalCompareMatchState() {
    const pairsRef = useRef(new Map());
    const [pairs, setPairs] = useState(() => pairsRef.current);
    const [dirtyState, setDirtyState] = useState(EMPTY_DIRTY_STATE);

    const replacePairs = useCallback((nextPairs) => {
        pairsRef.current = nextPairs;
        setPairs(nextPairs);
        setDirtyState(computeDirtyState(nextPairs));
        return nextPairs;
    }, []);

    const initPair = useCallback(({ trackId, referenceTrackId, matches = [], serverReferenceTrackId = referenceTrackId, force = false }) => {
        const pairKey = getPairKey(trackId, referenceTrackId);
        if (!pairKey) {
            return null;
        }

        const currentPair = pairsRef.current.get(pairKey);
        if (!force && currentPair && isPairDirty(currentPair)) {
            return currentPair;
        }

        const nextPair = makePair(trackId, referenceTrackId, matches, { serverReferenceTrackId });
        if (!force && currentPair && currentPair.serverSignature === nextPair.serverSignature && asKey(currentPair.serverReferenceTrackId) === asKey(nextPair.serverReferenceTrackId)) {
            return currentPair;
        }
        const nextPairs = new Map(pairsRef.current);
        nextPairs.set(pairKey, nextPair);
        replacePairs(nextPairs);
        return nextPair;
    }, [replacePairs]);

    const discardPair = useCallback((trackId, referenceTrackId) => {
        const pairKey = getPairKey(trackId, referenceTrackId);
        if (!pairKey || !pairsRef.current.has(pairKey)) {
            return;
        }
        const nextPairs = new Map(pairsRef.current);
        nextPairs.delete(pairKey);
        replacePairs(nextPairs);
    }, [replacePairs]);

    const discardTrack = useCallback((trackId) => {
        const trackKey = asKey(trackId);
        if (!trackKey) {
            return;
        }
        const nextPairs = new Map(pairsRef.current);
        let changed = false;
        nextPairs.forEach((pair, pairKey) => {
            if (asKey(pair.trackId) === trackKey || asKey(pair.referenceTrackId) === trackKey) {
                nextPairs.delete(pairKey);
                changed = true;
            }
        });
        if (changed) {
            replacePairs(nextPairs);
        }
    }, [replacePairs]);

    const applyOperation = useCallback((trackId, referenceTrackId, producer, meta = {}) => {
        const pairKey = getPairKey(trackId, referenceTrackId);
        if (!pairKey) {
            return null;
        }

        let operationResult = null;
        const currentPairs = pairsRef.current;
        const currentPair = currentPairs.get(pairKey) || makePair(trackId, referenceTrackId, []);
        const beforeMatches = currentPair.localMatches;
        const afterMatches = normalizeMatches(producer(beforeMatches), trackId, referenceTrackId);
        if (matchSignature(beforeMatches) === matchSignature(afterMatches)) {
            return currentPair;
        }

        const nextPair = {
            ...currentPair,
            initialized: true,
            localMatches: afterMatches,
            undoStack: pushOperation(currentPair.undoStack, {
                type: meta.type || "UPDATE_COMPARE_MATCHES",
                label: meta.label || "更新 Compare 匹配",
                before: beforeMatches,
                after: afterMatches,
                timestamp: Date.now(),
            }),
            redoStack: [],
        };
        const nextPairs = new Map(currentPairs);
        nextPairs.set(pairKey, nextPair);
        replacePairs(nextPairs);
        operationResult = nextPair;
        return operationResult;
    }, [replacePairs]);

    const setManualMatched = useCallback(({ trackId, referenceTrackId, scheduleDraftFlightId, sourceOperationId }) => applyOperation(
        trackId,
        referenceTrackId,
        (matches) => [
            ...removeConflictingMatches(matches, scheduleDraftFlightId, sourceOperationId),
            createManualMatch({ trackId, referenceTrackId, scheduleDraftFlightId, sourceOperationId, matchStatus: "MATCHED" }),
        ],
        { type: "MANUAL_MATCHED", label: "人工建立匹配" },
    ), [applyOperation]);

    const markManualNew = useCallback(({ trackId, referenceTrackId, scheduleDraftFlightId }) => applyOperation(
        trackId,
        referenceTrackId,
        (matches) => [
            ...removeConflictingMatches(matches, scheduleDraftFlightId, null),
            createManualMatch({ trackId, referenceTrackId, scheduleDraftFlightId, matchStatus: "NEW" }),
        ],
        { type: "MANUAL_NEW", label: "标记新增" },
    ), [applyOperation]);

    const markManualCancelled = useCallback(({ trackId, referenceTrackId, sourceOperationId }) => applyOperation(
        trackId,
        referenceTrackId,
        (matches) => [
            ...removeConflictingMatches(matches, null, sourceOperationId),
            createManualMatch({ trackId, referenceTrackId, sourceOperationId, matchStatus: "CANCELLED" }),
        ],
        { type: "MANUAL_CANCELLED", label: "标记取消" },
    ), [applyOperation]);

    const markManualIgnore = useCallback(({ trackId, referenceTrackId, role, scheduleDraftFlightId = null, sourceOperationId = null }) => {
        const nextDraftFlightId = role === "editing" ? scheduleDraftFlightId : null;
        const nextSourceOperationId = role === "reference" ? sourceOperationId : null;
        return applyOperation(
            trackId,
            referenceTrackId,
            (matches) => [
                ...removeConflictingMatches(matches, nextDraftFlightId, nextSourceOperationId),
                createManualMatch({
                    trackId,
                    referenceTrackId,
                    scheduleDraftFlightId: nextDraftFlightId,
                    sourceOperationId: nextSourceOperationId,
                    matchStatus: "IGNORE",
                }),
            ],
            { type: "MANUAL_IGNORE", label: "标记忽略" },
        );
    }, [applyOperation]);

    const deleteLocalMatch = useCallback(({ trackId, referenceTrackId, match }) => {
        const identity = getMatchIdentity(match);
        if (!identity) {
            return null;
        }
        return applyOperation(
            trackId,
            referenceTrackId,
            (matches) => matches.filter((item) => getMatchIdentity(item) !== identity),
            { type: "DELETE_COMPARE_MATCH", label: "解除匹配" },
        );
    }, [applyOperation]);

    const resetManualMatches = useCallback(({ trackId, referenceTrackId }) => applyOperation(
        trackId,
        referenceTrackId,
        (matches) => matches.filter((match) => normalizeType(match) !== "MANUAL"),
        { type: "RESET_MANUAL_COMPARE_MATCHES", label: "重置人工匹配" },
    ), [applyOperation]);

    const applyAutoPreview = useCallback(({ trackId, referenceTrackId, matches = [] }) => applyOperation(
        trackId,
        referenceTrackId,
        (currentMatches) => {
            const manualMatches = currentMatches.filter((match) => normalizeType(match) === "MANUAL");
            const autoMatches = normalizeMatches(matches, trackId, referenceTrackId)
                .filter((match) => normalizeType(match) === "AUTO");
            return [...manualMatches, ...autoMatches];
        },
        { type: "AUTO_COMPARE_PREVIEW", label: "自动匹配预览" },
    ), [applyOperation]);

    const undoPair = useCallback((trackId, referenceTrackId) => {
        const pairKey = getPairKey(trackId, referenceTrackId);
        if (!pairKey) {
            return null;
        }
        const currentPair = pairsRef.current.get(pairKey);
        if (!currentPair?.undoStack?.length) {
            return null;
        }
        const operation = currentPair.undoStack[currentPair.undoStack.length - 1];
        const nextPairs = updatePairInMap(pairsRef.current, pairKey, (pair) => ({
            ...pair,
            localMatches: operation.before,
            undoStack: pair.undoStack.slice(0, -1),
            redoStack: pushOperation(pair.redoStack, operation),
        }));
        replacePairs(nextPairs);
        return operation;
    }, [replacePairs]);

    const redoPair = useCallback((trackId, referenceTrackId) => {
        const pairKey = getPairKey(trackId, referenceTrackId);
        if (!pairKey) {
            return null;
        }
        const currentPair = pairsRef.current.get(pairKey);
        if (!currentPair?.redoStack?.length) {
            return null;
        }
        const operation = currentPair.redoStack[currentPair.redoStack.length - 1];
        const nextPairs = updatePairInMap(pairsRef.current, pairKey, (pair) => ({
            ...pair,
            localMatches: operation.after,
            undoStack: pushOperation(pair.undoStack, operation),
            redoStack: pair.redoStack.slice(0, -1),
        }));
        replacePairs(nextPairs);
        return operation;
    }, [replacePairs]);

    const resetPairAfterSave = useCallback(({ trackId, referenceTrackId, matches = [] }) => initPair({ trackId, referenceTrackId, matches, serverReferenceTrackId: referenceTrackId, force: true }), [initPair]);

    const getPairState = useCallback((trackId, referenceTrackId) => {
        const pairKey = getPairKey(trackId, referenceTrackId);
        return pairKey ? pairs.get(pairKey) || null : null;
    }, [pairs]);

    const getDirtyPairs = useCallback(() => Array.from(dirtyState.dirtyPairKeys)
        .map((pairKey) => pairsRef.current.get(pairKey))
        .filter(Boolean), [dirtyState.dirtyPairKeys]);

    const getManualDecisions = useCallback((trackId, referenceTrackId) => {
        const pair = pairsRef.current.get(getPairKey(trackId, referenceTrackId));
        return (pair?.localMatches || [])
            .filter((match) => normalizeType(match) === "MANUAL")
            .map(toPayloadMatch);
    }, []);

    const getCommitMatches = useCallback((trackId, referenceTrackId) => {
        const pair = pairsRef.current.get(getPairKey(trackId, referenceTrackId));
        return (pair?.localMatches || []).map(toPayloadMatch);
    }, []);

    return useMemo(() => ({
        matchServerState: pairs,
        matchLocalState: pairs,
        dirtyState,
        pairs,
        initPair,
        discardPair,
        discardTrack,
        getPairState,
        getDirtyPairs,
        getManualDecisions,
        getCommitMatches,
        setManualMatched,
        markManualNew,
        markManualCancelled,
        markManualIgnore,
        deleteLocalMatch,
        resetManualMatches,
        applyAutoPreview,
        undoPair,
        redoPair,
        resetPairAfterSave,
    }), [
        applyAutoPreview,
        deleteLocalMatch,
        dirtyState,
        discardPair,
        discardTrack,
        getCommitMatches,
        getDirtyPairs,
        getManualDecisions,
        getPairState,
        initPair,
        markManualCancelled,
        markManualIgnore,
        markManualNew,
        pairs,
        redoPair,
        resetManualMatches,
        resetPairAfterSave,
        setManualMatched,
        undoPair,
    ]);
}

export default useLocalCompareMatchState;
